import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiMachineImportResult } from '../../types';
import { apiClient } from '../../services/apiClient';
import { downloadCsvErrors } from '../../utils/csvErrorsDownload';
import './SelectionModal.css';

interface MultiMachineCsvImportModalProps {
  reportId: string;
  onClose: () => void;
}

type ModalStatus = 'idle' | 'processing' | 'done';

const MultiMachineCsvImportModal: React.FC<MultiMachineCsvImportModalProps> = ({ reportId, onClose }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<ModalStatus>('idle');
  const [result, setResult] = useState<MultiMachineImportResult | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    if (selected) {
      const isCsv = selected.name.toLowerCase().endsWith('.csv') || selected.type === 'text/csv';
      if (!isCsv) {
        setFatalError('invalid_file_type');
        setStatus('done');
        return;
      }
    }
    setFile(selected);
  };

  const handleImport = async () => {
    if (!file) return;
    setStatus('processing');
    try {
      const data = await apiClient.importSparePartsMultiMachine(reportId, file);
      setResult(data);
      setStatus('done');
    } catch (err: any) {
      setFatalError(err.message || 'unknown_error');
      setStatus('done');
    }
  };

  const handleOverlayClick = () => {
    if (status === 'idle') onClose();
  };

  const handleDownloadErrors = () => {
    if (!file || !result || result.errors.length === 0) return;
    downloadCsvErrors(file, result.errors);
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-left">
            <h3>{t('reportForm.multiMachineCsvImportModal.title')}</h3>
          </div>
          {status === 'done' && (
            <button onClick={onClose} className="modal-close">&times;</button>
          )}
        </div>

        <div className="modal-body">
          {status === 'idle' && (
            <div className="csv-import-idle">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div className="csv-file-row">
                <button
                  type="button"
                  className="btn-select-small"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('reportForm.multiMachineCsvImportModal.selectFile')}
                </button>
                <span className="csv-filename">
                  {file ? file.name : t('attachmentsForm.noFile')}
                </span>
              </div>
              <div className="csv-import-actions">
                <button
                  type="button"
                  className="btn-add"
                  onClick={handleImport}
                  disabled={!file}
                >
                  <span className="material-icons">upload_file</span> {t('reportForm.multiMachineCsvImportModal.import')}
                </button>
              </div>
            </div>
          )}

          {status === 'processing' && (
            <div className="csv-import-processing">
              <span className="btn-spinner"></span>
              <span>{t('reportForm.multiMachineCsvImportModal.processing')}</span>
            </div>
          )}

          {status === 'done' && fatalError !== null && (
            <div className="csv-import-fatal-error">
              <span className="material-icons" style={{ color: 'var(--color-error, #d32f2f)', fontSize: '2rem' }}>error</span>
              <p className="error-message">{t(`messages.csvImportFatalError.${fatalError}`, fatalError)}</p>
            </div>
          )}

          {status === 'done' && result !== null && (
            <div className="csv-import-results">
              <h4>{t('reportForm.multiMachineCsvImportModal.results')}</h4>
              <table className="spareparts-table csv-summary-table">
                <tbody>
                  <tr>
                    <td>{t('reportForm.multiMachineCsvImportModal.totalRows')}</td>
                    <td><strong>{result.totalRows}</strong></td>
                  </tr>
                  <tr>
                    <td>{t('reportForm.multiMachineCsvImportModal.insertedCount')}</td>
                    <td><strong>{result.insertedCount}</strong></td>
                  </tr>
                  <tr>
                    <td>{t('reportForm.multiMachineCsvImportModal.errorCount')}</td>
                    <td><strong>{result.errorCount}</strong></td>
                  </tr>
                </tbody>
              </table>

              {result.errors.length > 0 && (
                <>
                  <div className="csv-download-errors-row">
                    <button type="button" className="btn-download-errors" onClick={handleDownloadErrors}>
                      <span className="material-icons">download</span> {t('reportForm.multiMachineCsvImportModal.downloadErrors')}
                    </button>
                  </div>
                  <h4>{t('reportForm.multiMachineCsvImportModal.errorsTable')}</h4>
                  <div className="spareparts-table-container">
                    <table className="spareparts-table">
                      <thead>
                        <tr>
                          <th>{t('reportForm.multiMachineCsvImportModal.colRow')}</th>
                          <th>{t('reportForm.multiMachineCsvImportModal.colSerialNumber')}</th>
                          <th>{t('reportForm.multiMachineCsvImportModal.colErrorCode')}</th>
                          <th>{t('reportForm.multiMachineCsvImportModal.colMessage')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((err, i) => (
                          <tr key={i}>
                            <td>{err.rowNumber}</td>
                            <td>{err.serialNumber ?? '—'}</td>
                            <td>{t(`messages.csvImportErrorCode.${err.errorCode}`, err.errorCode)}</td>
                            <td>{err.errorMessage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {status === 'done' && (
          <div className="modal-footer">
            <button type="button" className="btn-save" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiMachineCsvImportModal;
